import {
  Box,
  Button,
  Card,
  Container,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import Iconify from '@src/components/iconify';
import Scrollbar from '@src/components/scrollbar';
import { ReferralStatusChip, RewardStatusChip } from './StatusChip';
import { ReferredUser, ReferralStatus } from './types';

const MOCK_USERS: ReferredUser[] = [
  { userId: 781, displayName: 'LuckyPlay',  referredBy: 'user123', referredById: 123, registrationDate: '10-01-2024 10:24 AM', status: 'Qualified',   hasPurchased: true,  rewardStatus: 'Claimed' },
  { userId: 651, displayName: 'SlotQueen',  referredBy: 'user1512', referredById: 1512, registrationDate: '10-03-2024 08:41 AM', status: 'Registered',  hasPurchased: false, rewardStatus: 'Not Claimed' },
  { userId: 421, displayName: 'CloverWin',  referredBy: 'user36',  referredById: 36, registrationDate: '10-04-2024 02:11 PM', status: 'Qualified',   hasPurchased: true,  rewardStatus: 'Claimed' },
  { userId: 842, displayName: 'ReelHero',   referredBy: 'user123', referredById: 123, registrationDate: '10-05-2024 01:24 PM', status: 'Inactive',    hasPurchased: false, rewardStatus: '-' },
  { userId: 102, displayName: 'Jackpot7',   referredBy: 'user11',  referredById: 11, registrationDate: '10-06-2024 06:33 PM', status: 'Qualified',   hasPurchased: true,  rewardStatus: 'Claimed' },
];

const TABS = ['Referred Users', 'Qualified Referrals', 'Inactive Referrals', 'Purchase Conversions'];

const TAB_STATUS_MAP: Record<number, ReferralStatus | null> = {
  0: null,
  1: 'Qualified',
  2: 'Inactive',
  3: null,
};

const SUMMARY_CARDS = [
  { title: 'New Registrations',         subtitle: 'Attributed to Referrals',         value: '1,248',          icon: 'mdi:account-plus-outline',    color: '#5B7FFF', bg: '#EEF1FF', tab: 0 },
  { title: 'Qualified Referrals',        subtitle: 'Rewards Issued',                  value: '892',            icon: 'mdi:check-circle-outline',    color: '#2CB67D', bg: '#E8FBF3', tab: 1 },
  { title: 'Inactive Referrals',         subtitle: 'Registered (Inactive)',           value: '356',            icon: 'mdi:account-off-outline',     color: '#E74C3C', bg: '#FDECEA', tab: 2 },
  { title: 'First Purchase Conversions', subtitle: 'users who made their first purchase', value: '624',        icon: 'mdi:cart-outline',            color: '#9B59B6', bg: '#F5EEF8', tab: 3 },
  { title: 'Total Bonus Coins',          subtitle: 'Awarded (Silver)',                value: '888,888,150,000', icon: 'mdi:alpha-s-circle',         color: '#3498DB', bg: '#EBF5FB', tab: null },
  { title: 'Total Bonus Coins',          subtitle: 'Awarded (Gold)',                  value: '300,000',        icon: 'mdi:alpha-g-circle',          color: '#F39C12', bg: '#FEF9E7', tab: null },
];

export default function ReferAFriend() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const filtered = MOCK_USERS.filter((u) => {
    const matchSearch = search === '' || String(u.userId).includes(search);
    const statusFilter = TAB_STATUS_MAP[activeTab];
    const matchStatus = statusFilter === null
      ? activeTab === 3 ? u.hasPurchased : true
      : u.status === statusFilter;
    return matchSearch && matchStatus;
  });

  const tabCounts = [
    MOCK_USERS.length,
    MOCK_USERS.filter((u) => u.status === 'Qualified').length,
    MOCK_USERS.filter((u) => u.status === 'Inactive').length,
    MOCK_USERS.filter((u) => u.hasPurchased).length,
  ];

  return (
    <Container maxWidth="xl">
      <Helmet><title>Refer a Friend</title></Helmet>

      {/* Summary Cards */}
      <Grid container spacing={2} mb={3}>
        {SUMMARY_CARDS.map((card, i) => (
          <Grid item xs={12} sm={6} md={4} key={i}>
            <Card sx={{ p: 2.5, borderRadius: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', position: 'relative' }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box sx={{
                  width: 52, height: 52, borderRadius: '50%',
                  backgroundColor: card.bg,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  <Iconify icon={card.icon} width={28} sx={{ color: card.color }} />
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary" fontWeight={500} fontSize="0.78rem">{card.title}</Typography>
                  <Typography variant="caption" color="text.disabled" fontSize="0.7rem">{card.subtitle}</Typography>
                  <Typography variant="h6" fontWeight={700} mt={0.5} fontSize="1.1rem">{card.value}</Typography>
                </Box>
              </Stack>
              {card.tab !== null && (
                <Button
                  size="small"
                  endIcon={<Iconify icon="mdi:chevron-right" width={16} />}
                  onClick={() => setActiveTab(card.tab as number)}
                  sx={{ position: 'absolute', bottom: 10, right: 10, fontSize: '0.7rem', color: '#5B7FFF', textTransform: 'none' }}
                >
                  View Data
                </Button>
              )}
            </Card>
          </Grid>
        ))}
      </Grid>

      <Card sx={{ borderRadius: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
        {/* Tabs */}
        <Tabs
          value={activeTab}
          onChange={(_, v) => { setActiveTab(v); setPage(0); }}
          sx={{ px: 2, borderBottom: '1px solid #F0F0F0' }}
        >
          {TABS.map((tab, i) => (
            <Tab
              key={tab}
              label={
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <span>{tab}</span>
                  <Box sx={{
                    backgroundColor: activeTab === i ? '#5B7FFF' : '#EEEEEE',
                    color: activeTab === i ? '#fff' : '#666',
                    borderRadius: '12px', px: 0.8, py: 0.1,
                    fontSize: '0.7rem', fontWeight: 600,
                  }}>
                    {tabCounts[i]}
                  </Box>
                </Stack>
              }
              sx={{ textTransform: 'none', fontWeight: 500, fontSize: '0.8rem' }}
            />
          ))}
        </Tabs>

        {/* Search + Actions */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" p={2} spacing={2}>
          <TextField
            size="small"
            placeholder="Search User ID"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Iconify icon="mdi:magnify" width={20} />
                </InputAdornment>
              ),
            }}
            sx={{ width: 260, '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
          />
          <Stack direction="row" spacing={1}>
            <Button
              variant="text"
              startIcon={<Iconify icon="mdi:filter-variant" width={18} />}
              sx={{ color: '#333', textTransform: 'none' }}
            >
              Filters
            </Button>
            <IconButton>
              <Iconify icon="mdi:refresh" width={20} />
            </IconButton>
            <Button
              variant="contained"
              sx={{ backgroundColor: '#6A00AC', borderRadius: '8px', textTransform: 'none', px: 3 }}
            >
              Export
            </Button>
          </Stack>
        </Stack>

        {/* Table */}
        <Scrollbar>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 700, color: '#5B7FFF', backgroundColor: '#FAFAFA', borderBottom: 'none' } }}>
                  <TableCell>User ID</TableCell>
                  <TableCell>Display Name</TableCell>
                  <TableCell>Referred By</TableCell>
                  <TableCell>Registration Date</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Purchase</TableCell>
                  <TableCell>Reward Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => (
                  <TableRow key={row.userId} sx={{ '& td': { borderBottom: '1px solid #F5F5F5', py: 2.5 } }}>
                    <TableCell>
                      <Typography
                        variant="body2" fontWeight={600} color="#5B7FFF" sx={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/dashboard/refer-a-friend/referred-user/${row.userId}`)}
                      >
                        {row.userId}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{row.displayName}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2" color="#5B7FFF" sx={{ cursor: 'pointer', fontWeight: 500 }}
                        onClick={() => navigate(`/dashboard/refer-a-friend/referrer/${row.referredById}`)}
                      >
                        {row.referredBy}
                      </Typography>
                    </TableCell>
                    <TableCell><Typography variant="body2" color="text.secondary">{row.registrationDate}</Typography></TableCell>
                    <TableCell><ReferralStatusChip status={row.status} /></TableCell>
                    <TableCell><Typography variant="body2">{row.hasPurchased ? 'Yes' : 'No'}</Typography></TableCell>
                    <TableCell><RewardStatusChip status={row.rewardStatus} /></TableCell>
                    <TableCell>
                      <IconButton size="small">
                        <Iconify icon="mdi:dots-vertical" width={20} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {filtered.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      No referral records found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Scrollbar>

        <TablePagination
          component="div"
          count={filtered.length}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={(_, p) => setPage(p)}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50]}
        />
      </Card>
    </Container>
  );
}
